// The app shell: owns which screen is mounted, the one-second tick, and the
// blocking reconnect overlay.
//
// ONE TICK FOR THE WHOLE APP, not a timer per countdown. Every countdown reads
// `deadline - now`, so they all need the same thing (a reason to re-read the
// clock) and none of them needs its own interval. It also means a suspended
// WebView resumes with a single catch-up render instead of N drifted timers.

import type { MatchController, MatchViewState, Phase } from '../state/match';
import { el, setText } from './dom';
import { screenFor, type Screen, type ScreenDeps } from './screens';

const TICK_MS = 1000;

export class App {
  private readonly screenHost = el('div', { class: 'screen-host' });
  private readonly overlay: HTMLElement;
  private readonly overlayText = el('p', { class: 'muted' }, ['']);

  private screen: Screen | null = null;
  private mountedPhase: Phase | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly mount: HTMLElement,
    private readonly controller: MatchController,
    private readonly deps: ScreenDeps,
  ) {
    // The reconnect overlay is INPUT-BLOCKING and sits above every screen,
    // rather than each screen handling a disconnected state. A player on a
    // frozen screen who can still type is a player submitting into a socket
    // that is not there.
    this.overlay = el('div', { class: 'overlay', hidden: 'hidden' }, [
      el('div', { class: 'overlay-card' }, [
        el('div', { class: 'spinner', role: 'status' }),
        el('p', { class: 'title' }, ['Reconnecting…']),
        this.overlayText,
      ]),
    ]);

    this.mount.replaceChildren(this.screenHost, this.overlay);
  }

  start(): void {
    this.controller.store.subscribe((state) => this.render(state));
    this.render(this.controller.store.get());
    this.timer = setInterval(() => this.render(this.controller.store.get()), TICK_MS);
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.screen?.dispose?.();
  }

  private render(state: MatchViewState): void {
    if (state.phase !== this.mountedPhase) {
      this.mountedPhase = state.phase;
      this.screen?.dispose?.();
      this.screen = screenFor(state.phase, this.deps);
      this.screenHost.replaceChildren(this.screen.root);
    }
    // `now` is read ONCE per render and passed down, so every countdown on
    // screen agrees. Reading Date.now() per component lets two timers on the
    // same frame show different seconds.
    this.screen?.update(state, Date.now());

    this.overlay.hidden = !state.reconnecting;
    setText(
      this.overlayText,
      "Hold on — you keep your place in the match for a few seconds.",
    );
  }
}

export type { ScreenDeps };
